import React from 'react';
import { Edit, Trash2, ChevronUp, ChevronDown, Bookmark, Copy } from 'lucide-react';
import { NavbarComponent } from './NavbarComponent';
import { NavigationMenuComponent } from './NavigationMenuComponent';
import { HeroComponent } from './HeroComponent';
import { SplitHeroComponent } from './SplitHeroComponent';
import { SimpleHeaderComponent } from './SimpleHeaderComponent';
import { FeaturesComponent } from './FeaturesComponent';
import { CtaComponent } from './CtaComponent';
import { FooterComponent } from './FooterComponent';
import { FormComponent } from './FormComponent';
import { FormSplitComponent } from './FormSplitComponent';
import { TextBlocksComponent } from './TextBlocksComponent';
import { TeamComponentRenderer } from './TeamComponent';
import { MediaTextComponent } from './MediaTextComponent';
import { PricingComponent } from './PricingComponent';
import { VideoComponent } from './VideoComponent';
import { TitleSubtitleComponent } from './TitleSubtitleComponent';
import { ContactInfoComponent } from './ContactInfoComponent';
import { HeadContentComponent } from './HeadContentComponent';
import { BodyEndComponent } from './BodyEndComponent';
import { HtmlEmbedComponent } from './HtmlEmbedComponent';
import { HtmlSplitComponent } from './HtmlSplitComponent';
import { FaqComponent } from './FaqComponent';
import { TestimonialsComponent } from './TestimonialsComponent';
import { CountdownTimerComponent } from './CountdownTimerComponent';
import { ImageGalleryComponent } from './ImageGalleryComponent';
import { ImageInfoComponent } from './ImageInfoComponent';
import { ImageCardComponent } from './ImageCardComponent';
import { ContentAreaComponent } from './ContentAreaComponent';
import { PromoBannerComponent } from './PromoBannerComponent';
import { LogocloudComponent } from './LogocloudComponent';
import { StatsComponent } from './StatsComponent';
import { BlogPreviewComponent } from './BlogPreviewComponent';
import { ConversionPopupComponent } from './ConversionPopupComponent';
import { CompeteFormComponent } from './CompeteFormComponent';
import type { Component } from '../schemas/validation';

interface ComponentRendererProps {
  component: Component;
  isEditing?: boolean;
  onEdit?: (component: Component) => void;
  onDelete?: (componentId: string) => void;
  onMoveUp?: (componentId: string) => void;
  onMoveDown?: (componentId: string) => void;
  onSaveToLibrary?: (component: Component) => void;
  onDuplicate?: (component: Component) => void;
}

// Error boundary component for individual components.
// Uses a resetKey prop so that when component data changes (e.g. source type
// switch), the boundary can recover instead of staying stuck in the error state.
class ComponentErrorBoundary extends React.Component<
  { children: React.ReactNode; componentType: string; componentId: string; resetKey?: string },
  { hasError: boolean; error?: Error; lastResetKey?: string }
> {
  constructor(props: {
    children: React.ReactNode;
    componentType: string;
    componentId: string;
    resetKey?: string;
  }) {
    super(props);
    this.state = { hasError: false, lastResetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(
    props: { resetKey?: string },
    state: { hasError: boolean; lastResetKey?: string }
  ) {
    if (state.hasError && props.resetKey !== state.lastResetKey) {
      return { hasError: false, error: undefined, lastResetKey: props.resetKey };
    }
    if (props.resetKey !== state.lastResetKey) {
      return { lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      `Error rendering ${this.props.componentType} component (${this.props.componentId}):`,
      error,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-2">
          <div className="flex items-center space-x-2 text-red-800">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">Component Error</span>
          </div>
          <p className="text-red-700 text-sm mt-1">
            Failed to render {this.props.componentType} component. Please check the component data
            or contact support.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  component,
  isEditing = false,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  onSaveToLibrary,
  onDuplicate,
}) => {
  const renderComponent = () => {
    try {
      switch (component.type) {
        case 'navbar':
          return <NavbarComponent component={component} />;
        case 'navigation-menu':
          return <NavigationMenuComponent component={component} />;
        case 'hero':
          return <HeroComponent component={component} />;
        case 'split-hero':
          return <SplitHeroComponent component={component} />;
        case 'simple-header':
          return <SimpleHeaderComponent component={component} />;
        case 'features':
          return <FeaturesComponent component={component} />;
        case 'cta':
          return <CtaComponent component={component} />;
        case 'footer':
          return <FooterComponent component={component} />;
        case 'form':
          return <FormComponent component={component} />;
        case 'form-split':
          return <FormSplitComponent component={component} />;
        case 'team':
          return <TeamComponentRenderer component={component} />;
        case 'media-text':
          return <MediaTextComponent component={component} />;
        case 'pricing':
          return <PricingComponent component={component} />;
        case 'video':
          return <VideoComponent component={component} />;
        case 'text-blocks':
          return <TextBlocksComponent component={component} />;
        case 'title-subtitle':
          return <TitleSubtitleComponent component={component} />;
        case 'contact-info':
          return <ContactInfoComponent component={component} />;
        case 'head-content':
          return <HeadContentComponent component={component} />;
        case 'body-end':
          return <BodyEndComponent component={component} />;
        case 'html-embed':
          return <HtmlEmbedComponent component={component} isEditing={isEditing} />;
        case 'html-split':
          return <HtmlSplitComponent component={component} />;
        case 'faq':
          return <FaqComponent component={component} />;
        case 'testimonials':
          return <TestimonialsComponent component={component} />;
        case 'countdown-timer':
          return <CountdownTimerComponent component={component} isEditing={isEditing} />;
        case 'image-gallery':
          return <ImageGalleryComponent component={component} />;
        case 'image-info':
          return <ImageInfoComponent component={component} />;
        case 'image-card':
          return <ImageCardComponent component={component} />;
        case 'content-area':
          return <ContentAreaComponent component={component} isEditing={isEditing} />;
        case 'promo-banner':
          return <PromoBannerComponent component={component} isEditing={isEditing} />;
        case 'logocloud':
          return <LogocloudComponent component={component} />;
        case 'stats':
          return <StatsComponent component={component} />;
        case 'blog-preview':
          return <BlogPreviewComponent component={component} />;
        case 'conversion-popup':
          return <ConversionPopupComponent component={component} isEditing={isEditing} />;
        case 'compete-form':
          return <CompeteFormComponent component={component} />;
        default:
          console.warn(`Unknown component type: ${(component as any).type}`);
          return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 m-2">
              <div className="text-yellow-800">
                <span className="font-medium">Unknown component type:</span>{' '}
                {(component as any).type}
              </div>
            </div>
          );
      }
    } catch (error) {
      console.error(`Error rendering component ${component.type} (${component.id}):`, error);
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-2">
          <div className="text-red-800">
            <span className="font-medium">Render Error:</span> Failed to render {component.type}{' '}
            component
          </div>
        </div>
      );
    }
  };

  // Derive a reset key so the error boundary can recover when component
  // configuration changes (e.g. video source type switch that caused the crash).
  const errorResetKey = `${component.id}-${(component.data as any)?.source?.sourceType ?? ''}-${!!(component.data as any)?.source?.url}`;

  // If not in editing mode, just render the component with error boundary
  if (!isEditing) {
    // For navbar components with sticky enabled, apply sticky to the wrapper div
    const isNavbarSticky =
      (component.type === 'navbar' || component.type === 'navigation-menu') &&
      (component.data as any)?.sticky !== false;
    const wrapperClasses = isNavbarSticky ? 'sticky top-0 z-50' : '';

    return (
      <ComponentErrorBoundary
        componentType={component.type}
        componentId={component.id}
        resetKey={errorResetKey}
      >
        <div
          key={component.id}
          id={`section-${component.id}`}
          data-component-id={component.id}
          data-component-type={component.type}
          className={wrapperClasses}
        >
          {renderComponent()}
        </div>
      </ComponentErrorBoundary>
    );
  }

  // In editing mode, wrap with editing controls and error boundary
  return (
    <ComponentErrorBoundary
      componentType={component.type}
      componentId={component.id}
      resetKey={errorResetKey}
    >
      <div
        key={component.id}
        id={`section-${component.id}`}
        data-component-id={component.id}
        data-component-type={component.type}
        className="relative group"
      >
        {/* Component Content */}
        <div>{renderComponent()}</div>

        {/* Editing Toolbar - Top Right Corner */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-200 z-[70]">
          <div className="flex items-center space-x-1 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg p-1">
            {/* Edit Button */}
            {onEdit && (
              <button
                onClick={() => onEdit(component)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Edit component"
              >
                <Edit size={16} />
              </button>
            )}

            {/* Move Up Button */}
            {onMoveUp && (
              <button
                onClick={() => onMoveUp(component.id)}
                className="p-2 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                title="Move up"
              >
                <ChevronUp size={16} />
              </button>
            )}

            {/* Move Down Button */}
            {onMoveDown && (
              <button
                onClick={() => onMoveDown(component.id)}
                className="p-2 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                title="Move down"
              >
                <ChevronDown size={16} />
              </button>
            )}

            {/* Save to Library Button */}
            {onSaveToLibrary && (
              <button
                onClick={() => onSaveToLibrary(component)}
                className="p-2 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                title="Save to library"
              >
                <Bookmark size={16} />
              </button>
            )}

            {/* Duplicate Button */}
            {onDuplicate && (
              <button
                onClick={() => onDuplicate(component)}
                className="p-2 text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
                title="Duplicate component"
              >
                <Copy size={16} />
              </button>
            )}

            {/* Delete Button */}
            {onDelete && (
              <button
                onClick={() => onDelete(component.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Delete component"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>

          {/* Component Type Label */}
          <div className="mt-2 px-2 py-1 text-xs rounded-md capitalize backdrop-blur-sm bg-gray-900/90 text-white">
            {component.type}
          </div>
        </div>

        {/* Hover Border */}
        <div className="absolute inset-0 border-2 border-blue-400/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
      </div>
    </ComponentErrorBoundary>
  );
};
